/**
 * Format a number as a price with the given locale and currency
 * @param price - The price to format
 * @param locale - The locale to use for formatting (defaults to 'nl-NL')
 * @param currency - The currency code to use (defaults to 'EUR')
 * @returns Formatted price string
 */
export const formatPrice = (price: number, locale = 'nl-NL', currency = 'EUR'): string => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(price);
};

/**
 * Parse a formatted price string back to a number
 * @param price - The formatted price string to parse
 * @param locale - The locale to use for parsing (defaults to 'nl-NL')
 * @returns Parsed price number
 */
export const parsePrice = (price: string, locale = 'nl-NL'): number => {
    // Remove currency symbols and spaces
    const cleanPrice = price.replace(/[^0-9,.-]/g, '');

    // Parse based on locale
    const numberFormat = new Intl.NumberFormat(locale);
    const parts = numberFormat.formatToParts(1234.56);
    const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value ?? '.';
    const groupSeparator = parts.find((part) => part.type === 'group')?.value ?? ',';

    // Replace locale-specific separators with standard ones
    const standardized = cleanPrice
        .replace(new RegExp(`\\${groupSeparator}`, 'g'), '')
        .replace(new RegExp(`\\${decimalSeparator}`), '.');

    return parseFloat(standardized);
};

/**
 * Calculate the discount percentage
 * @param originalPrice - The original price
 * @param discountedPrice - The price after discount
 * @returns Discount percentage
 */
export const calculateDiscountPercentage = (
    originalPrice: number,
    discountedPrice: number
): number => {
    if (originalPrice <= 0) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
};

/**
 * Format a price range
 * @param minPrice - The minimum price
 * @param maxPrice - The maximum price
 * @param locale - The locale to use for formatting (defaults to 'nl-NL')
 * @param currency - The currency code to use (defaults to 'EUR')
 * @returns Formatted price range string
 */
export const formatPriceRange = (
    minPrice: number,
    maxPrice: number,
    locale = 'nl-NL',
    currency = 'EUR'
): string => {
    if (minPrice === maxPrice) {
        return formatPrice(minPrice, locale, currency);
    }
    return `${formatPrice(minPrice, locale, currency)} - ${formatPrice(maxPrice, locale, currency)}`;
};
